import { useEffect, useMemo, useState } from 'react';
import { Card, Divider, Skeleton } from 'antd';
import { format, isValid } from 'date-fns';
import { AlertIcon, FileIcon } from '@dynamika-shared/ui/icons';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { EDTFDrawer } from '@dynamika-shared/components/EDTFDrawer/EDTFDrawer';
import { useParams } from 'react-router-dom';
import { twJoin } from '@utils/twMerge';
import { useQCTaskUploadsListSlice } from '@restore/QCTaskUploadsList/QCTaskUploadsList.slice';
import { DXAWorkstation } from 'src/pages/QC/QCUploads/DXAWorkstation';
import { UploadFileTypeEnum } from '@dynamika-shared/types/inventory';
import { QCTaskUpload } from '@dynamika-shared/types/qcTaskUpload';
import { Dot } from '@dynamika-shared/ui/Dot/Dot';

const ignoreWarningsForTypes = new Set([UploadFileTypeEnum.DXA]);

const styles = {
  columnWrapper: 'flex items-center justify-between mb-1 last:mb-0',
  left: 'flex w-[50%] text-dimGray ml-[10px] text-1624',
  right: 'flex w-[50%] justify-start text-1624 font-medium',
};
export const QCUploads = () => {
  const [taskUploadsData, loading, edtfData, fetchEdtf] =
    useQCTaskUploadsListSlice((state, actions) => [
      state.data,
      state.loading,
      state.edtf.data,
      actions.fetchEDTF,
    ]);

  const { id: clinicalTrialId, taskId } = useParams<{
    id: string;
    taskId: string;
  }>();

  const [edtfId, setEdtfId] = useState('');

  const activeData = useMemo(
    () => taskUploadsData?.find(upload => upload.taskUploadId === edtfId),
    [taskUploadsData, edtfId]
  );

  useEffect(() => {
    if (clinicalTrialId && taskId && edtfId) {
      fetchEdtf({ clinicalTrialId, taskId, uploadId: edtfId });
    }
  }, [clinicalTrialId, taskId, edtfId]);

  const renderWarnings = (upload: QCTaskUpload) => {
    if (upload.fileType === UploadFileTypeEnum.DXA) {
      const successVerifications = upload.verifications?.filter(
        verification => verification.status === 'SUCCESS'
      );

      if (!successVerifications?.length) {
        return null;
      }

      return null;
    }

    if (ignoreWarningsForTypes.has(upload.fileType)) {
      return null;
    }
    const warnings = upload.verifications?.filter(
      verification => verification.status !== 'SUCCESS'
    );

    return (
      <>
        <Divider />
        {!warnings?.length ? (
          <div className={styles.columnWrapper}>
            <div className={styles.left}>Warnings</div>
            <div className={styles.right}>No warnings</div>
          </div>
        ) : (
          warnings.map(({ title, comment, id }) => (
            <div key={id}>
              <div className={styles.columnWrapper}>
                <div className={styles.left}>
                  <AlertIcon className='mr-[8px] mt-[3px] inline-flex text-redOrange' />
                  {title}
                </div>
                <div className={styles.right}>{comment}</div>
              </div>
            </div>
          ))
        )}
      </>
    );
  };
  const sortedUploads = useMemo(() => {
    return [...(taskUploadsData ?? [])].sort((a, b) => {
      const dateA = new Date(a.uploadDate ?? '');
      const dateB = new Date(b.uploadDate ?? '');

      if (isValid(dateA) && isValid(dateB)) {
        return dateB.getTime() - dateA.getTime();
      }
      return 1;
    });
  }, [taskUploadsData]);
  return (
    <>
      <div className='flex h-full w-[100%] flex-col'>
        <div className='flex-1 overflow-auto'>
          {!loading ? (
            sortedUploads?.map(upload => {
              const {
                taskUploadId,
                siteUserName,
                uploadDate,
                comment,
                fileType,
              } = upload;
              return (
                <Card key={taskUploadId} className='pb-[24px] pl-1 pr-2 mb-2'>
                  <div className='mb-[22px] flex items-center justify-between font-medium rounded-[8px] bg-[#F6F9FF] px-[12px] text-1824 border-[1px] border-brightGray'>
                    <div className='flex items-center'>
                      <div>Uploaded by {siteUserName} </div>
                      <div className='flex'>
                        <Dot className='!mx-0.5 text-[24px]' />
                      </div>
                      <div
                        className={twJoin(
                          styles.right,
                          'justify-start text-dimGray flex-1'
                        )}
                      >
                        <span className='whitespace-nowrap'>
                          {format(new Date(uploadDate), 'dd-MMM-yyyy')}
                        </span>
                        <div>
                          <Dot className='!mx-0.5 text-[24px]' />
                        </div>
                        <span>{format(new Date(uploadDate), 'HH:mm:ss')}</span>
                      </div>
                    </div>
                    <Button
                      title={'edtfId'}
                      type='link'
                      onClick={() => setEdtfId(taskUploadId)}
                    >
                      <span className='text-sm font-medium'>View eDTF</span>
                    </Button>
                  </div>
                  <div className={styles.columnWrapper}>
                    <div className={styles.left}>
                      <div className='flex items-center gap-1'>
                        <FileIcon /> File format
                      </div>
                    </div>
                    <div
                      className={twJoin(
                        styles.right,
                        'flex items-center gap-2 justify-between'
                      )}
                    >
                      <span>{fileType}</span>
                      {taskId && clinicalTrialId && (
                        <DXAWorkstation
                          upload={upload}
                          clinicalTrialId={clinicalTrialId}
                          taskId={taskId}
                          uploadId={taskUploadId}
                        />
                      )}
                    </div>
                  </div>
                  {renderWarnings(upload)}
                  {comment && (
                    <>
                      <Divider />
                      <div className={twJoin(styles.columnWrapper, 'pb-2')}>
                        <div className={styles.left}>Comment</div>
                        <div className={styles.right}>{comment}</div>
                      </div>
                    </>
                  )}
                </Card>
              );
            })
          ) : (
            <Skeleton active />
          )}
        </div>
      </div>
      <EDTFDrawer
        isOpen={!!edtfId}
        uploadDateTime={activeData?.uploadDate}
        edtfData={edtfData}
        onClose={() => setEdtfId('')}
        siteUserName={activeData?.siteUserName ?? 'unknown user'}
      />
    </>
  );
};
